/**
 * Utilities for classifying catalog items by tier:
 *   - Parent (container): has sized variants as children; cannot back an asset directly
 *   - Variant (child): has a parent_id set; IS a leaf that assets can reference
 *   - Standalone: neither parent nor variant; IS a leaf that assets can reference
 *
 * Parent status is derived structurally: a catalog is a parent if its id appears as
 * a parent_id on any other catalog in the collection. No static flag is required.
 */

export interface CatalogClassifiable {
  id: string;
  entity_data?: {
    parent_id?: string | null;
    [key: string]: unknown;
  } | null;
}

/**
 * Builds a Set of catalog IDs that are parents within the given collection.
 * Use this before filtering loops to avoid O(n²) re-computation.
 */
export function buildParentIdSet(items: CatalogClassifiable[]): Set<string> {
  const ids = new Set<string>();
  for (const item of items) {
    const pid = item.entity_data?.parent_id;
    if (typeof pid === 'string' && pid.trim().length > 0) ids.add(pid.trim());
  }
  return ids;
}

/** Returns true when the catalog item is a parent/container within the given collection. */
export function isCatalogVariant(item: CatalogClassifiable): boolean {
  const parentId = item.entity_data?.parent_id;
  return typeof parentId === 'string' && parentId.trim().length > 0;
}
